import ApiService from '@/common/api.service'
import unitAttachmentsModule from './unitAttachmentsModule'
import unitPhotosModule from './unitPhotosModule'
import unitNotesModule from './unitNotesModule'
import store from '@/store/index'

import * as utils from '@/common/utils'
import * as notesHelpers from '@/store/notes/notes.helpers'
import * as attachmentsHelpers from '@/store/attachments/attachments.helpers'
import * as photosHelpers from '@/store/photos/photos.helpers'
import * as unitsHelper from './unitsHelper'
import * as security from '@/common/security'
import * as findSimCardsWidgetModuleHelpers from '@/store/sim-cards/findSimCardsWidget.helpers'
import * as gdspoFindSimCardsWidgetModuleHelpers from '@/store/sim-cards/gdspo/gdspoFindSimCardsWidget.helpers'

const module = 'units/unitsModule'
const notesModule = module + '/notes'
const attachmentsModule = module + '/attachments'
const photosModule = module + '/photos'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return unitsHelper.properties()
}

function formDefaults () {
  return {
    partners: [],
    unit_types: [],
    unit_models: [],
    unit_statuses: [],
    sensorData: [],
    unit_groups: [],
    timezones: {},
    sim_card_providers: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param name String
 *
 * @return Object
 * @param partner_id
 * @param agency_id
 * @param client_id
 */
function transformCreateGroup (name, partner_id, agency_id, client_id) {
  return {
    id: null,
    name: name,
    partner_id: partner_id,
    agency_id: agency_id,
    client_id: client_id
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: unitsHelper.properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  },

  /**
   * Returns true if the vehicles is loading
   *
   * @return Boolean
   */
  isVehiclesLoading (state) {
    return state.data.isVehiclesLoading
  },

  /**
   * Returns true if the gateway routing is loading
   *
   * @return Boolean
   */
  isGatewayRoutesLoading (state) {
    return state.data.isGatewayRoutesLoading
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`units/${id}`)
      .then((response) => {
        context.commit('resetSimCard')
        context.commit('set', response.data.data)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        photosHelpers.setPhotos(photosModule, response.data.data.photos)
        context.commit('setSavedClientId', response.data.data.client_id)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a full view of the unit (this includes additional
   * data to properly render the view).
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchView (context, id) {
    return ApiService
      .get(`units/${id}/view/?includes=telemetry`)
      .then((response) => {
        let unit = unitsHelper.transformLoad(response.data.data)
        context.commit('set', unit)
        context.commit('setOriginal', unit)
        context.commit('setFormData', response.data.data)
        context.commit('setSavedClientId', response.data.data.client_id)
        context.commit('setIsModified', false)

        notesHelpers.setNotes(notesModule, response.data.data.notes)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        photosHelpers.setPhotos(photosModule, response.data.data.photos)

        return response
      })
  },

  /**
   * Request a archived record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchArchiveUnit (context, id) {
    return ApiService
      .get(`units/archives/${id}/unarchive/view`)
      .then((response) => {
        let unit = unitsHelper.transformLoad(response.data.data)

        context.commit('resetSimCard')
        context.commit('set', unit)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        photosHelpers.setPhotos(photosModule, response.data.data.photos)
        context.commit('setSavedClientId', response.data.data.client_id)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "`create`" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`units/create/`)
      .then((response) => {
        notesHelpers.purge(notesModule)
        attachmentsHelpers.purge(attachmentsModule)
        photosHelpers.purge(photosModule)

        context.commit('resetSimCard')
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Fetches unit groups linked to client selected in org
   *
   * @param context
   * @param id
   */
  fetchUnitGroups (context, id) {
    let unit = context.getters['get']
    return ApiService
      .get(`units/groups/listing/by-client/${id}`)
      .then((response) => {
        context.commit(`setUnitGroups`, response.data.data)

        let is_select_tag = isNaN(utils.asNumberOrNull(unit.unit_group.id))
        if (is_select_tag) {
          context.commit('setUnitGroupId', null)
        }
      })
  },

  /**
   * Fetches vehicles by client id
   *
   * @param context
   * @param clientId
   */
  fetchVehiclesByClient (context, clientId) {
    context.commit('setVehiclesLoading', true)

    return ApiService
      .get(`vehicles/listing/by-client/${clientId}`)
      .then((response) => {
        context.commit('setVehicleAssignments', response.data.data)
        context.commit('setVehiclesLoading', false)
      }).catch(() => {
        context.commit('setVehiclesLoading', false)
      })
  },

  /**
   * Fetches gateway routing
   *
   * @param context
   * @param partnerId
   * @param agencyId
   * @param clientId
   */
  fetchGatewayRoutes (context, {partnerId, agencyId, clientId}) {
    context.commit('setGatewayRoutesLoading', true)

    let url = `gateways/routes?partner_id=__null__&agency_id=__null__&client_id=__null__`
    if (clientId !== null) {
      url = `gateways/routes?client_id=${clientId}`
    } else if (agencyId !== null) {
      url = `gateways/routes?agency_id=${agencyId}`
    } else if (partnerId !== null) {
      url = `gateways/routes?partner_id=${partnerId}`
    }

    return ApiService
      .get(url)
      .then((response) => {
        context.commit('setGatewayRouteAssignments', response.data.data)
        context.commit('setGatewayRoutesLoading', false)
      }).catch(() => {
        context.commit('setGatewayRoutesLoading', false)
      })
  },

  /**
   * Request a basic unit record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchBasicUnitEdit (context, id) {
    return ApiService
      .get(`units/basic/${id}/edit/?includes=telemetry`)
      .then((response) => {
        let unit = unitsHelper.transformBasicLoad(response.data.data)

        context.commit('set', unit)
        context.commit('setOriginal', unit)
        context.commit('setIsModified', false)

        ApiService
          .get(`units/groups/listing/by-client/${unit.client.id}`)
          .then((response) => {
            if (response.data.data.length > 0) {
              context.commit(`setUnitGroups`, response.data.data)
            }
          })

        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`units/${id}/edit/?includes=telemetry`)
      .then((response) => {
        let unit = unitsHelper.transformLoad(response.data.data)

        context.commit('resetSimCard')
        context.commit('set', unit)
        context.commit('setOriginal', unit)
        context.commit('setFormData', response.data.data)
        context.commit('setProp', {
          name: 'vehicleIsAssigned',
          value: response.data.data.vehicle !== null,
          modifiesState: false
        })
        context.commit('setProp', {
          name: 'gatewayRouteIsAssigned',
          value: response.data.data.gateway_route !== null,
          modifiesState: false
        })
        context.commit('setSavedClientId', unit.client.id)
        context.commit('setIsModified', false)

        notesHelpers.setNotes(notesModule, response.data.data.notes)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        photosHelpers.setPhotos(photosModule, response.data.data.photos)

        ApiService
          .get(`units/groups/listing/by-client/${unit.client.id}`)
          .then((response) => {
            if (response.data.data.length > 0) {
              context.commit(`setUnitGroups`, response.data.data)
            }

            if (response.data.data.client_id !== null) {
              return context.dispatch('fetchVehiclesByClient', unit.client.id)
            }

            return response
          })

        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let unit = context.getters['get']
    let attachments = attachmentsHelpers.attachments(attachmentsModule)
    let photos = photosHelpers.photos(photosModule)

    let notes = []
    let note = context.getters['get'].note || null
    if (note !== null && note.trim() !== '') {
      notes.push(note)
    }

    return ApiService
      .post(`units`, unitsHelper.transformCreate(unit, 'unit', notes, attachments, photos))
      .then((response) => {
        notesHelpers.purge(notesModule)
        attachmentsHelpers.purge(attachmentsModule)
        photosHelpers.purge(photosModule)
        // context.commit('set', defaults())
        context.commit('resetGeneralOnly')
        context.commit('resetSimCard')
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, unitsHelper.transformLoad(response.data.data))

        if (unit.client.id !== null) {
          ApiService
            .get(`vehicles/listing/by-client/${unit.client.id}`)
            .then((vehicle_response) => {
              context.commit('setProp', {
                name: 'vehicleAssignments',
                value: vehicle_response.data.data,
                modifiesState: false
              })
              context.commit('setProp', {
                name: 'assigned_as_id',
                value: null,
                modifiesState: false
              })
              context.commit('setProp', {
                name: 'vehicle_id',
                value: utils.asNumberOrNull(null),
                modifiesState: false
              })
            })
        }

        return response
      })
  },

  /**
   * POST (create) new unit group record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @param data
   * @return Promise
   */
  postUnitGroup (context, data) {
    let group = transformCreateGroup(
      data.data.name,
      data.data.partner_id,
      data.data.agency_id,
      data.data.client_id
    )

    return ApiService
      .post(`units/groups`, group)
      .then((response) => {
        ApiService
          .get(`units/groups/listing/by-client/${response.data.data.client.id}`)
          .then((group_response) => {
            context.commit(`setUnitGroups`, group_response.data.data)
            context.commit('setUnitGroupId', response.data.data.id)
          })
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    let unit = context.getters['get']
    return ApiService
      .put(`units/${unit.id}`, unitsHelper.transformUpdate(unit))
      .then((response) => {
        context.commit('setSavedClientId', response.data.data.client_id)
        context.commit('setProp', {
          name: 'vehicle',
          value: response.data.data.vehicle,
          modifiesState: false
        })
        context.commit('setProp', {
          name: 'vehicleIsAssigned',
          value: response.data.data.vehicle !== null,
          modifiesState: false
        })
        context.commit('setProp', {
          name: 'gatewayRoute',
          value: response.data.data.gateway_route,
          modifiesState: false
        })
        context.commit('setProp', {
          name: 'gatewayRouteIsAssigned',
          value: response.data.data.gateway_route !== null,
          modifiesState: false
        })
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * PUT (update) the basic data for an existing unit through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  putBasic (context) {
    let unit = context.getters['get']

    return ApiService
      .put(`units/basic/${unit.id}`, unitsHelper.transformBasicUpdate(unit))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    let id = context.getters['get'].id
    return ApiService
      .delete(`units/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`units`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries a listing of units by client id from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryListingByClient (context, params) {
    return ApiService
      .get(`units/listing/by-client/${params.client_id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryHistory (context, params) {
    const id = context.getters['get'].id

    return ApiService
      .get(`units/${id}/history`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign({}, data)
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners,
      unit_types: data.unit_types,
      unit_models: data.unit_models,
      unit_statuses: data.unit_statuses,
      sensorData: data.sensor_data,
      unit_groups: [],
      timezones: data.timezones,
      sim_card_providers: data.sim_card_providers
    }
  },

  setUnitGroupId (state, id) {
    state.data.unit_group.id = id
  },

  setVehicleAssignments (state, vehicleAssignments) {
    state.data.vehicleAssignments = vehicleAssignments
  },

  setGatewayRouteAssignments (state, gatewayRouteAssignments) {
    state.data.gatewayRouteAssignments = gatewayRouteAssignments
  },

  setVehicleAssignedAsId (state, assignedAsId) {
    state.data.assigned_as_id = assignedAsId
  },

  setVehicleId (state, vehicleId) {
    state.data.vehicle_id = vehicleId
  },

  setGatewayRouteId (state, gatewayRouteId) {
    state.data.gateway_route_id_id = gatewayRouteId
  },

  setVehiclesLoading (state, isLoading) {
    state.data.isVehiclesLoading = isLoading
  },

  setGatewayRoutesLoading (state, isLoading) {
    state.data.isGatewayRoutesLoading = isLoading
  },

  /**
   * Set the unit's partner data.
   *
   * @param state
   * @param partner
   */
  setPartner (state, partner) {
    store.commit('units/unitsModule/clearAssignments')
    if (security.isAbovePartner()) {
      state.data.partner.id = partner.id
      state.data.partner.name = partner.name
      state.isModified = true
    }
  },

  /**
   * Set the unit's partner and agency data.
   *
   * @param state
   * @param agency
   */
  setAgency (state, agency) {
    store.commit('units/unitsModule/clearAssignments')
    if (security.isAbovePartner()) {
      state.data.partner.id = agency.partner.id
      state.data.partner.name = agency.partner.name
      state.isModified = true
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = agency.id
      state.data.agency.name = agency.name
      state.isModified = true
    }
  },

  /**
   * Set the unit's partner, agency and client data.
   *
   * @param state
   * @param client
   */
  setClient (state, client) {
    store.commit('units/unitsModule/clearAssignments')
    if (security.isAbovePartner()) {
      state.data.partner.id = client.partner.id
      state.data.partner.name = client.partner.name
      state.isModified = true
    }
    if (security.isAboveAgency() && client.agency.id !== null) {
      state.data.agency.id = client.agency.id
      state.data.agency.name = client.agency.name
      state.isModified = true
    }
    if (security.isAboveClient() || security.belongsToMultipleClients()) {
      state.data.client.id = client.id
      state.data.client.name = client.name
      state.isModified = true
    }
  },

  /**
   * Set the saved client id. This is to determine whether a unit is now
   * actually assigned to a client or not and saved that way. If so the
   * unit can no longer be assigned to another client except through
   * unit swapping.
   *
   * @param state
   * @param clientId
   */
  setSavedClientId (state, clientId) {
    state.data.savedClientId = clientId
  },

  /**
   * Clear the unit's partner, agency and client data.
   *
   * @param state
   */
  clearAssignments (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
    state.isModified = true
  },

  /**
   * Clears all previously loaded unit groups.
   *
   * @param state
   */
  clearUnitGroups (state) {
    state.form.unit_groups = []
    state.data.unit_group.id = null
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   * @param modifiesState Boolean
   */
  setProp (state, { name, value, modifiesState = true }) {
    if (modifiesState) {
      state.isModified = true
    }
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Sets the unit groups in form.
   *
   * @param state   Object
   * @param groups Array of objects in the form { id: 1, name: 'xyz' }
   */
  setUnitGroups (state, groups) {
    state.form.unit_groups = groups
  },

  /**
   * Transforms the unit_group state from null to object.
   * This is done because if no unit group was assigned to
   * the unit the unit group is set to null if
   * formFetchEdit is called instead of {id: null, name: ''}.
   * This causes an error when user tries to assign a unit group
   * in the edit screens.
   *
   * @param state   Object
   */
  setGroupDefaults (state) {
    state.data.unit_group = {
      id: null,
      name: ''
    }
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = Object.assign({}, defaults())
    state.original = unitsHelper.properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets only the general fields.
   *
   * @param state
   */
  resetGeneralOnly (state) {
    state.data.id = null
    state.data.imei = null
    state.data.serial_no = null
    state.data.stock_no = null
    state.data.name = null
    state.data.photos = []
    state.data.vehicle = null
    state.data.vehicle_id = null
    state.data.assigned_as_id = null
    state.data.gateway_route_id = null
    state.data.vehicleAssignments = []
    state.data.vehicleIsAssigned = false
    state.data.isVehiclesLoading = false
    state.data.gatewayRouteAssignments = []
    state.data.gatewayRouteIsAssigned = false
    state.data.isGatewayRoutesLoading = false
  },

  /**
   * Resets the SIM card field.
   *
   * @param state
   */
  resetSimCard (state) {
    state.data.sim_card.id = null
    state.data.sim_card.cell_no = null
    state.data.sim_card.iccid = null
    state.data.sim_card.gdsp_id = null
    state.data.sim_card.provider_id = null
    state.data.sim_card.provider_code = null
    state.data.sim_card.provider_name = null
    state.data.sim_card.note = null
    utils.storeReset('units/unitSimCardsModule')
    utils.storeReset('simCards/simCardsModule')
    utils.storeResetForm('simCards/simCardsModule')
    utils.storeReset('simCards/gdspoSimCardsModule')
    utils.storeResetForm('simCards/gdspoSimCardsModule')
    findSimCardsWidgetModuleHelpers.clearSelectedSimCards('sim_cards')
    gdspoFindSimCardsWidgetModuleHelpers.clearSelectedSimCards('sim_cards')
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = Object.assign({}, formDefaults())
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    notes: unitNotesModule,
    attachments: unitAttachmentsModule,
    photos: unitPhotosModule
  }
}



// WEBPACK FOOTER //
// ./src/store/units/unitsModule.js